import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { CatalogOption, PassChangeRow } from "../models/PassChanges";

// Timezone code used by the report SPs' IH.ToLocal conversion (same constant the
// other migrated reports use).
const TIME_ZONE_CODE = "EDTIND";

export interface GetPassChangesParams {
	start: string;
	end: string;
	standId?: number | null;
	passChangeReasonId?: number | null;
	productSizeId?: number | null;
	productTypeId?: number | null;
	signal?: AbortSignal;
}

export const getPassChanges = async ({
	start,
	end,
	standId = null,
	passChangeReasonId = null,
	productSizeId = null,
	productTypeId = null,
	signal,
}: GetPassChangesParams): Promise<PassChangeRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "standId", value: standId },
		{ name: "crewId", value: null },
		{ name: "passChangeReasonId", value: passChangeReasonId },
		{ name: "productSizeId", value: productSizeId },
		{ name: "productTypeId", value: productTypeId },
	];
	// No timeZoneCode: the procedure returns UTC and the grid converts. Only the
	// legacy procedure behind the proxy took one.
	const spName = "[MILL].[GetPassChangesDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching pass changes");
	}
	return get(resp, "data.tables[0].rows", []) as PassChangeRow[];
};

const fetchCatalog = async (
	sp: string,
	signal?: AbortSignal,
): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		sp,
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	throw new Error(resp.message || `Error fetching ${sp}`);
};

export const getProductTypes = ({ signal }: { signal?: AbortSignal } = {}) =>
	fetchCatalog("[MILL].[GetProductTypes]", signal);

export const getProductSizes = ({ signal }: { signal?: AbortSignal } = {}) =>
	fetchCatalog("[MILL].[GetProductSizes]", signal);

export const getPassChangeReasons = ({
	signal,
}: {
	signal?: AbortSignal;
} = {}) => fetchCatalog("[MILL].[GetPassChangeReasons]", signal);
